import React, { memo } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';

import { ErrorBoundary, ForPage404 } from '@monorepo/common';

import { book } from './books';
import { LegacyAccountRedirect, legacyEmployerPaths } from './legacyAccountRedirects';
import { PostJobListing } from '../pages/PostJobListing';
import { PrepaidPurchase } from '../pages/PrepaidPurchase';
import { EditJob } from '../pages/EditJob';
import { UpgradeListing } from '../pages/UpgradeListing';
import { ReactivateListing } from '../pages/ReactivateListing';
import { PostPrepaidListing } from '../pages/PostPrepaidListing';
import { Receipt } from '../pages/Receipt';
import { NoRemaining } from '../pages/NoRemaining';
import { PrepaidListingExpired } from '../pages/PrepaidListingExpired';
import { JobsAccountGuard } from '../components/JobsAccount';
import { JobsListing } from '../pages/JobsListing';
import { JobsPrepaid } from '../pages/JobsPrepaid';
import { JobsPrepaidDetail } from '../pages/JobsPrepaidDetail';
import { JobsApplicants } from '../pages/JobsApplicants';
import { JobsApplicantDetail } from '../pages/JobsApplicantDetail';
import { JobsAccountHome } from '../pages/JobsAccountHome';
import { JobsAccountApplicants } from '../pages/JobsAccountApplicants';
import { MyApplications } from '../pages/MyApplications';
import { SavedJobs } from '../pages/SavedJobs';
import { MyResume } from '../pages/MyResume';
import { ResumeDatabase } from '../pages/ResumeDatabase';
import { ResumeDatabaseDetail } from '../pages/ResumeDatabase/ResumeDetail';
import { ResumeContactResponse } from '../pages/ResumeContactResponse';

const RootRouters = () => (
  <>
    <Switch>
      <Route
        exact
        path="/"
      >
        <Redirect to={book.jobsAccount} />
      </Route>

      <Route
        exact
        path={[book.jobs, book.jobsNosh, book.jobsBrewbound, book.jobsBevnet]}
      >
        <ErrorBoundary>
          <PrepaidPurchase />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={[book.postAJob, book.postAJobBrewbound, book.postAJobNosh, book.postAJobBevnet]}
      >
        <ErrorBoundary>
          <PostJobListing />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={[book.editJob(), book.editJobBrewbound(), book.editJobNosh()]}
      >
        <ErrorBoundary>
          <EditJob />
        </ErrorBoundary>
      </Route>
      <Route
        exact
        path={[book.upgradeJob(), book.upgradeJobBrewbound(), book.upgradeJobNosh()]}
      >
        <ErrorBoundary>
          <UpgradeListing />
        </ErrorBoundary>
      </Route>

      <Route
        exact
        path={[book.reactivateListing, book.reactivateListingBrewbound, book.reactivateListingNosh]}
      >
        <ErrorBoundary>
          <ReactivateListing />
        </ErrorBoundary>
      </Route>

      <Route
        path={[book.postPrepaidListing(), book.postPrepaidListingBrewbound(), book.postPrepaidListingNosh()]}
        exact
      >
        <ErrorBoundary>
          <PostPrepaidListing />
        </ErrorBoundary>
      </Route>

      <Route
        path={[book.receipt, book.receiptBrewbound, book.receiptNosh]}
        exact
      >
        <ErrorBoundary>
          <Receipt />
        </ErrorBoundary>
      </Route>

      <Route
        path={[book.noremaining]}
        exact
      >
        <ErrorBoundary>
          <NoRemaining />
        </ErrorBoundary>
      </Route>

      <Route
        path={[book.prepaidListingExpired]}
        exact
      >
        <ErrorBoundary>
          <PrepaidListingExpired />
        </ErrorBoundary>
      </Route>

      <Route exact path={book.resumeContact()}>
        <ErrorBoundary>
          <ResumeContactResponse />
        </ErrorBoundary>
      </Route>

      {/* Before the guard so the login returnUrl carries the new path. */}
      <Route
        exact
        path={legacyEmployerPaths}
      >
        <LegacyAccountRedirect />
      </Route>

      <Route path="/user">
        <JobsAccountGuard>
          <Switch>
            <Route
              exact
              path={[
                book.jobsAccount,
                book.jobsEmployer,
                book.jobsJobSeeker,
              ]}
            >
              <ErrorBoundary>
                <JobsAccountHome />
              </ErrorBoundary>
            </Route>

            <Route exact path={book.myApplications}>
              <ErrorBoundary>
                <MyApplications />
              </ErrorBoundary>
            </Route>

            <Route exact path={book.savedJobs}>
              <ErrorBoundary>
                <SavedJobs />
              </ErrorBoundary>
            </Route>

            <Route exact path={book.myResume}>
              <ErrorBoundary>
                <MyResume />
              </ErrorBoundary>
            </Route>

            <Route exact path={book.resumeDatabaseDetail()}>
              <ErrorBoundary>
                <ResumeDatabaseDetail />
              </ErrorBoundary>
            </Route>

            <Route exact path={book.resumeDatabase}>
              <ErrorBoundary>
                <ResumeDatabase />
              </ErrorBoundary>
            </Route>

            <Route exact path={book.jobsPrepaidDetail()}>
              <ErrorBoundary>
                <JobsPrepaidDetail />
              </ErrorBoundary>
            </Route>

            <Route
              exact
              path={[book.jobsAllApplicants, book.jobsNewApplicants]}
            >
              <ErrorBoundary>
                <JobsAccountApplicants />
              </ErrorBoundary>
            </Route>

            <Route exact path={book.jobsApplicantDetail()}>
              <ErrorBoundary>
                <JobsApplicantDetail />
              </ErrorBoundary>
            </Route>

            <Route exact path={book.jobsApplicants()}>
              <ErrorBoundary>
                <JobsApplicants />
              </ErrorBoundary>
            </Route>

            <Route exact path={book.jobsPrepaid}>
              <ErrorBoundary>
                <JobsPrepaid />
              </ErrorBoundary>
            </Route>

            <Route exact path={book.jobsListings}>
              <ErrorBoundary>
                <JobsListing />
              </ErrorBoundary>
            </Route>

            <Route>
              <ForPage404 />
            </Route>
          </Switch>
        </JobsAccountGuard>
      </Route>

      <Route>
        <ForPage404 />
      </Route>

    </Switch>
  </>
);

export default memo(RootRouters);
